import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ToggleLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

export const FeaturesDialog = ({ company, onClose, onChanged }) => {
  const [features, setFeatures] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (company) api.ownerFeatures().then(setFeatures).catch(() => setFeatures([])); }, [company]);

  const toggle = async (key, enabled) => {
    setBusy(key);
    try { await api.ownerSetFeature(company.id, key, enabled); onChanged(company.id, key, enabled); toast.success(enabled ? "Feature switched on" : "Feature switched off"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change that feature"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="owner-features-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Extras · {company?.name}</DialogTitle>
          <DialogDescription>Switch a custom feature on for this company only. Other companies are not affected.</DialogDescription>
        </DialogHeader>
        {features === null ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : features.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center" data-testid="owner-features-empty">
              <ToggleLeft className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-2 font-medium">No custom features yet</p>
              <p className="text-sm text-muted-foreground">When a custom feature is built for a company, its switch appears here.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="owner-features-list">
              {features.map((f) => (
                <div key={f.key} className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                  <div><p className="text-sm font-medium">{f.label}</p>{f.description && <p className="text-xs text-muted-foreground">{f.description}</p>}</div>
                  <Switch checked={!!company.features?.[f.key]} disabled={busy === f.key} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`owner-feature-${f.key}`} />
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};
